/**
 * =============================================================================
 * IDS CRM Web - Time Reconciliation (L7)
 * =============================================================================
 *
 * Per engineer per day: clocked span vs hours logged on tickets/tasks, with
 * gap flags. work_logs + clock events are the single source of truth for
 * time; this view is where a manager spots the difference between the two.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toDateKey, fromDateKey, addDays, startOfWeek, formatDateKey } from '../utils/dates';
import BackToList from '../components/BackToList';
import './TimeReconciliation.css';

const FLAG_LABELS = {
  clocked_no_logs: 'Clocked in, nothing logged',
  logged_exceeds_clocked: 'Logged more than clocked',
  logs_without_clock: 'Logs without clocking in',
  large_unlogged_gap: 'Over 2h clocked but unlogged',
  // Phase 6 step 7: someone has saved Log Work that day not yet saved as hours.
  unfinished_log_work: 'Log Work not finished (not hours yet)'
};

function formatHours(ms) {
  if (!ms) return '0.0h';
  return `${(ms / 3600000).toFixed(1)}h`;
}

function TimeReconciliation() {
  const { authFetch } = useAuth();
  // 'YYYY-MM-DD' of the Monday, built from local calendar fields - the UTC
  // version sent Sunday after midnight in BST and slipped a day at each
  // clock change while paging weeks.
  const [weekStart, setWeekStart] = useState(() => toDateKey(startOfWeek(new Date())));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(`/api/v1/engineers/time-reconciliation?week_start=${weekStart}`);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to load reconciliation');
      }
      const data = await response.json();
      setRows(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, weekStart]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const shiftWeek = (daysDelta) => {
    setWeekStart(toDateKey(addDays(fromDateKey(weekStart), daysDelta)));
  };

  const flaggedCount = rows.filter(r => r.flags.length > 0).length;

  return (
    <div className="time-reconciliation-page">
      <div className="page-header">
        <div>
          <h1>Time Reconciliation</h1>
          <p>Clocked hours vs logged work per engineer per day — flags show where they disagree</p>
        </div>
        <BackToList to="/engineers" className="btn-secondary">← Engineers</BackToList>
      </div>

      <div className="recon-controls">
        <button className="btn-secondary" onClick={() => shiftWeek(-7)}>&larr; Previous week</button>
        <span className="recon-week-label">
          Week of {formatDateKey(weekStart, { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
        <button className="btn-secondary" onClick={() => shiftWeek(7)}>Next week &rarr;</button>
        {flaggedCount > 0 && (
          <span className="recon-flagged-count">{flaggedCount} day(s) flagged</span>
        )}
      </div>

      {error && <div className="recon-error">{error}</div>}

      {loading ? (
        <div className="loading">Loading reconciliation...</div>
      ) : rows.length === 0 ? (
        <div className="empty-message">No clocked or logged time in this week.</div>
      ) : (
        <div className="recon-table-wrap">
          <table className="recon-table">
            <thead>
              <tr>
                <th>Engineer</th>
                <th>Date</th>
                <th>Clocked</th>
                <th>Logged</th>
                <th>On-call</th>
                <th>Difference</th>
                <th>Logs</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={`${row.engineer_id}-${row.date}`} className={row.flags.length ? 'flagged' : ''}>
                  <td>
                    <Link to={`/engineers/${row.engineer_id}`}>{row.engineer_name}</Link>
                  </td>
                  <td>{formatDateKey(row.date, { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                  <td>{formatHours(row.clocked_ms)}</td>
                  <td>{formatHours(row.logged_ms)}</td>
                  <td>{row.on_call_ms ? formatHours(row.on_call_ms) : '-'}</td>
                  <td className={row.delta_ms < 0 ? 'delta-negative' : ''}>
                    {row.delta_ms >= 0 ? '+' : ''}{formatHours(Math.abs(row.delta_ms)).replace('h', '')}h
                  </td>
                  <td>{row.log_count}</td>
                  <td>
                    {row.flags.map(flag => (
                      <span key={flag} className="recon-flag">{FLAG_LABELS[flag] || flag}</span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TimeReconciliation;
